import { Button, Collapse, IconButton } from '@vrplatform/components';
import { Icon, type IconDefinition } from '@vrplatform/icons';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency, type Maybe } from '@vrplatform/utils';
import {
  Anchor,
  Box,
  Center,
  Group,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { type ComponentProps, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DrawerCollapsableTable } from '../../_components';
import {
  ReservationFeeAddModal,
  ReservationFeeDeleteModal,
} from '../_components';
import type { Reservation } from '../_queries/useLedgerReservationDetailDrawerQuery';
import { getFeeTypeRouteFromRecurringFeeEnum } from '../../../views/fees/overview/_utils';
import { FEE_TYPES } from '../../../views/fees/overview/_components';
import { FEE_RESERVATION_PREVIEW_SEARCH_KEY } from '../../../views/fees/edit/_components';
import { ReservationFinancialAccountTooltip } from './ReservationFinancialAccountTooltip';
import { ReservationPostingDatePreviewTooltip } from './ReservationPostingDatePreviewTooltip';
import { formatFinancialLockMessage } from '../_utils';
import { RESERVATION_ENTITY_SECTION_ROW_LIMIT } from '../_utils/reservation-entity-section-row-limit';
import { ReservationSectionJumpTo } from './ReservationSectionJumpTo';

function useFeeModals() {
  const [opened, setOpened] = useState<{
    id?: string;
    reservationId: string;
    recurringFeeId: string | null;
    lineTypeLabel?: string;
    description: string;
    centTotal: number | undefined;
    postingAt: Maybe<string>;
  } | null>(null);

  const [removeModal, setRemoveModal] = useState<{
    id: string;
    reservationId: string;
  } | null>(null);

  return {
    edit: {
      opened,
      setOpened,
    },
    remove: {
      opened: removeModal,
      setOpened: setRemoveModal,
    },
  };
}

export const ReservationFeesSection = ({
  rowData,
  currency,
  reservation,
  total,
  type,
  goToTab,
}: {
  rowData: Reservation['feesAndCommissions'];
  reservation: ComponentProps<typeof ReservationFeeAddModal>['reservation'];
  currency: Reservation['currency'];
  total: number;
  type: 'tab' | 'collapse';
  goToTab: () => void;
}) => {
  const reservationId = reservation.id;

  const theme = useMantineTheme();

  const { edit, remove } = useFeeModals();
  const openEditModal = edit.setOpened;
  const openRemoveModal = remove.setOpened;

  const columns = useMemo<
    MRT_ColumnDef<Reservation['feesAndCommissions'][0]>[]
  >(
    () => [
      {
        header: 'Title',
        accessorKey: 'title',
        Footer: () => (
          <Text span fw={500}>
            Total to PM
          </Text>
        ),
        mantineTableFooterCellProps: {
          pl: 'xs',
        },
        Cell: ({ row }) => {
          const goto = useNavigate();

          const recurringFee = row.original.recurringFee;

          const td =
            row.original.status === 'inactive' ? 'line-through' : undefined;

          const icon = ((): IconDefinition => {
            if (recurringFee?.type)
              return FEE_TYPES[
                getFeeTypeRouteFromRecurringFeeEnum(recurringFee.type)
              ].icon;

            return 'CircleDollarIcon';
          })();

          return (
            <ReservationFinancialAccountTooltip account={row.original.account}>
              <Group wrap="nowrap" gap="xs">
                <Center>
                  <Tooltip
                    disabled={!recurringFee?.id}
                    withArrow
                    zIndex={1000}
                    withinPortal
                    label={
                      recurringFee?.type &&
                      FEE_TYPES[
                        getFeeTypeRouteFromRecurringFeeEnum(recurringFee.type)
                      ].title
                    }
                  >
                    <Icon icon={icon} size={16} />
                  </Tooltip>
                </Center>

                <Box>
                  {recurringFee?.id ? (
                    <Tooltip
                      disabled={row.original.type === 'financial'}
                      label={recurringFee.name}
                      withArrow
                    >
                      <Anchor
                        component="button"
                        c="dark"
                        td={td}
                        ta="left"
                        onClick={() => {
                          const url = new URL(
                            `/fees-commission/${recurringFee!.id}/edit`,
                            window.location.origin
                          );

                          url.searchParams.set(
                            FEE_RESERVATION_PREVIEW_SEARCH_KEY,
                            reservationId
                          );

                          goto(url.pathname + url.search);
                        }}
                      >
                        {row.original.description}
                      </Anchor>
                    </Tooltip>
                  ) : (
                    <Text c="dark" ta="left" td={td}>
                      {row.original.description}
                    </Text>
                  )}
                  {row.original.type === 'adjustment' && (
                    <Text size="xs" c="gray" ta="left" td={td}>
                      {recurringFee?.id ? 'Fee Adjustment' : 'Financials Fee'}
                    </Text>
                  )}
                </Box>
              </Group>
            </ReservationFinancialAccountTooltip>
          );
        },
      },
      {
        header: 'amount',
        accessorKey: 'centTotal',
        maxSize: 90,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          pr: 'xs',
        },
        Cell: ({ row }) => {
          const financial = row.original;

          const amount = formatCurrency(financial.centTotal / 100, currency);

          const isAdjustment = financial.type === 'adjustment';

          const td =
            financial.status === 'inactive' ? 'line-through' : undefined;

          const locked = financial.lock?.status === 'locked';

          const lockReason = useMemo<string | undefined>(
            () => formatFinancialLockMessage(financial.lock),
            [financial.lock]
          );

          return (
            <Group gap={5} wrap="nowrap">
              {isAdjustment ? (
                <>
                  <IconButton
                    icon={'Edit3Icon'}
                    iconSize={16}
                    disabled={!financial.reservationLine?.id || locked}
                    tooltip={lockReason || 'Edit adjustment'}
                    className="row-action-icon edit-adjustment-icon"
                    onClick={() =>
                      financial.reservationLine?.id
                        ? openEditModal({
                            id: financial.reservationLine.id,
                            reservationId,
                            centTotal: financial.centTotal,
                            description: financial.description,
                            recurringFeeId: null,
                            lineTypeLabel:
                              financial.recurringFee?.name ??
                              financial.description.replace(
                                /\s+adjustment$/i,
                                ''
                              ),
                            postingAt: financial.postingAt ?? null,
                          })
                        : null
                    }
                  />
                  <IconButton
                    icon={'TrashIcon'}
                    iconSize={16}
                    disabled={locked}
                    tooltip={lockReason || 'Delete adjustment'}
                    className="row-action-icon delete-adjustment-icon"
                    onClick={() =>
                      financial.reservationLine?.id
                        ? openRemoveModal({
                            reservationId,
                            id: financial.reservationLine.id,
                          })
                        : null
                    }
                  />
                </>
              ) : (
                <IconButton
                  className={'row-action-icon add-adjustment-icon'}
                  icon={'PlusIcon'}
                  iconSize={16}
                  tooltip={'Add adjustment'}
                  onClick={() =>
                    openEditModal({
                      reservationId,
                      centTotal: undefined,
                      description: `${financial.description} adjustment`,
                      recurringFeeId: financial.recurringFee?.id ?? null,
                      postingAt: undefined,
                    })
                  }
                />
              )}

              <ReservationPostingDatePreviewTooltip
                postingDatePreview={financial.postingDatePreview}
                currency={currency}
              >
                <Text
                  ta="right"
                  sx={{
                    flexShrink: 0,
                  }}
                  td={td}
                >
                  {amount}
                </Text>
              </ReservationPostingDatePreviewTooltip>
            </Group>
          );
        },
        Footer: () => {
          return (
            <Text span fw={500} ta="right" display={'block'}>
              {formatCurrency(total / 100, currency)}
            </Text>
          );
        },
      },
    ],
    [currency, total, reservationId, openEditModal, openRemoveModal]
  );

  const rightSection = (
    <Button
      variant="light"
      size="xs"
      leftIcon="PlusIcon"
      color="neutral"
      iconColor={theme.colors.neutral[5]}
      onClick={() =>
        edit.setOpened({
          centTotal: undefined,
          description: '',
          reservationId,
          recurringFeeId: null,
          postingAt: undefined,
        })
      }
    >
      Add adjustment
    </Button>
  );

  const title = 'Fees and Commissions';

  if (
    type === 'collapse' &&
    rowData.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT
  )
    return (
      <ReservationSectionJumpTo
        title={title}
        count={rowData.length}
        goToTab={goToTab}
      />
    );

  return (
    <>
      <ReservationFeeAddModal
        reservation={reservation}
        closeModal={() => edit.setOpened(null)}
        recurringFee={edit.opened}
      />
      <ReservationFeeDeleteModal
        reservationId={reservationId}
        closeModal={() => remove.setOpened(null)}
        recurringFee={remove.opened}
      />

      {type === 'tab' ? (
        <>
          <Collapse
            title={
              <Text component="span" size="sm" fw={500}>
                {title}
                <Text c={'gray'} ml="xs" size="xs" component="span">
                  {rowData.length}
                </Text>
              </Text>
            }
            rightSection={rightSection}
            hideToggle
          />
          <LazyTable
            data={{
              rows: rowData,
              error: null,
              loading: false,
              rowCount: rowData.length,
            }}
            table={{
              columns,
              hideTopBar: true,
              hideHeader: true,
              enableStickyFooter: true,
            }}
          />
        </>
      ) : (
        <DrawerCollapsableTable
          title={title}
          rowData={rowData}
          columns={columns}
          rightSection={rightSection}
          emptyRowsFallback={() => (
            <Center>
              <Text size="sm" c="gray">
                No fees and commissions found
              </Text>
            </Center>
          )}
          styles={{
            row: () => {
              return {
                '.row-action-icon': {
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                },
                '&:hover .row-action-icon': {
                  opacity: 1,
                },
              };
            },
          }}
        />
      )}
    </>
  );
};
